import { skipToken } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { type ComponentProps, useMemo } from 'react';

import { useClosedDatesQuery, useDateFromTenorQuery } from '@/api/dates';
import { useCurrentUserQuery } from '@/api/user';
import type { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export function useDisabledDatePresets(tileId: string, currencyPair: string | undefined) {
  const { data: closedDatesData } = useClosedDatesQuery(isDefined(currencyPair) ? [currencyPair] : skipToken);

  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const { data: currentUser } = useCurrentUserQuery();

  const maxTenor = currentUser?.clients.find(({ companyId }) => companyId === clientId)?.maxTenor;

  const { data: maxDate } = useDateFromTenorQuery(
    isDefined(maxTenor) && isDefined(currencyPair) ? { tenor: maxTenor, currencyPair } : skipToken,
  );

  const closedDates = useMemo<string[] | undefined>(() => {
    if (isNotDefined(closedDatesData) || isNotDefined(currencyPair)) {
      return [];
    }

    return closedDatesData[currencyPair] ?? [];
  }, [closedDatesData, currencyPair]);

  return useMemo<ComponentProps<typeof SgDatePicker>['disablePresets']>(() => {
    return {
      weekends: true,
      past: true,
      dates: closedDates,
      ranges: isDefined(maxDate) ? [{ start: maxDate, end: '2999-01-01' }] : [],
    };
  }, [closedDates, maxDate]);
}
